// client/src/components/SeasonInfo.jsx
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { FaCalendarAlt, FaHourglassHalf, FaExclamationTriangle, FaCheckCircle } from 'react-icons/fa';
import { fetchCurrentSeason } from '../services/api';

const SeasonInfo = ({ competition, continent, title, icon }) => {
    const { t } = useTranslation();
    const [seasonInfo, setSeasonInfo] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchSeason = async () => {
            try {
                const res = await fetchCurrentSeason(competition, continent);
                setSeasonInfo(res.data);
            } catch (err) {
                console.error(err);
            } finally {
                setLoading(false);
            }
        };
        fetchSeason();
    }, [competition, continent]);

    if (loading) return null;
    if (!seasonInfo || !seasonInfo.start_date) return null;

    const startDate = new Date(seasonInfo.start_date);
    const endDate = seasonInfo.end_date ? new Date(seasonInfo.end_date) : null;
    const today = new Date();
    
    let status = 'active';
    let statusColor = 'text-green-400';
    let statusBg = 'bg-green-600/20';
    let progress = 0;
    let timeRemaining = null;
    let statusIcon = null;

    if (endDate) {
        if (today > endDate) {
            status = 'ended';
            statusColor = 'text-red-400';
            statusBg = 'bg-red-600/20';
            statusIcon = <FaExclamationTriangle className="text-red-400 h-3 w-3" />;
        } else if (today < startDate) {
            status = 'not_started';
            statusColor = 'text-yellow-400';
            statusBg = 'bg-yellow-600/20';
            statusIcon = <FaHourglassHalf className="text-yellow-400 h-3 w-3" />;
        } else {
            const total = endDate - startDate;
            const elapsed = today - startDate;
            progress = Math.min(100, Math.max(0, (elapsed / total) * 100));
            const daysRemaining = Math.ceil((endDate - today) / (1000 * 60 * 60 * 24));
            timeRemaining = t('season.daysRemaining', { days: daysRemaining, defaultValue: `${daysRemaining} days left` });
            statusIcon = <FaCheckCircle className="text-green-400 h-3 w-3" />;
        }
    }

    return (
        <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className={`bg-gradient-to-r from-gray-800/80 to-gray-900/80 backdrop-blur-sm rounded-xl p-2 mb-2 border shadow-md ${
                status === 'ended' ? 'border-red-500/30' : 'border-yellow-500/30'
            }`}
        >
            <div className="flex items-center justify-between flex-wrap gap-1">
                <div className="flex items-center gap-1.5">
                    <div className="text-yellow-400 text-sm">{icon}</div>
                    <div>
                        <p className="text-[10px] text-gray-400 leading-tight">
                            {t('season.seasonNumber', { number: seasonInfo.season_number })}
                        </p>
                        <p className="text-[11px] font-bold text-white flex items-center gap-1 leading-tight">
                            {title}
                            {status === 'active' && (
                                <span className="text-[10px] bg-green-600/50 text-green-200 px-1 py-0.5 rounded-full animate-pulse">
                                    {t('season.live')}
                                </span>
                            )}
                        </p>
                    </div>
                </div>

                {/* عرض أيقونة الحالة فقط بدون رسالة نصية طويلة */}
                {status !== 'active' && (
                    <div className={`flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded-full ${statusBg} ${statusColor}`}>
                        {statusIcon}
                        <span className="sr-only">{status}</span>
                    </div>
                )}

                <div className="flex items-center gap-2 text-[10px] text-gray-300">
                    <div className="flex items-center gap-0.5">
                        <FaCalendarAlt className="text-yellow-400 text-[10px]" />
                        <span>{t('season.started')} {startDate.toLocaleDateString()}</span>
                    </div>
                    {endDate && (
                        <div className="flex items-center gap-0.5">
                            <FaHourglassHalf className="text-yellow-400 text-[10px]" />
                            <span>{t('season.ends')} {endDate.toLocaleDateString()}</span>
                        </div>
                    )}
                </div>
            </div>

            {status === 'active' && endDate && (
                <>
                    <div className="mt-1 w-full bg-gray-700 rounded-full h-1">
                        <div
                            className="bg-yellow-400 h-1 rounded-full transition-all duration-500"
                            style={{ width: `${Math.min(100, progress)}%` }}
                        />
                    </div>
                    <div className="flex justify-between mt-0.5">
                        <span className="text-[9px] text-gray-500">{t('season.progress')}</span>
                        <span className="text-[9px] text-gray-500">{timeRemaining}</span>
                    </div>
                </>
            )}
        </motion.div>
    );
};

export default SeasonInfo;